import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pagesAPI, toursAPI, photosAPI } from '../services/api';
import Settings from '../components/admin/Settings';
import Languages from '../components/admin/Languages';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [pages, setPages] = useState([]);
  const [tours, setTours] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState('pages');
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [pagesRes, toursRes, photosRes] = await Promise.all([
        pagesAPI.getAllPages(),
        toursAPI.getAllTours(),
        photosAPI.getAllPhotos()
      ]);
      setPages(pagesRes.data);
      setTours(toursRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/admin/login');
  };

  const handleEditPage = (page) => {
    setEditingItem(page);
    setActiveTab('edit-page');
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    try {
      await pagesAPI.updatePage(editingItem.id, editingItem);
      alert('Page updated successfully!');
      loadData();
      setActiveTab('pages');
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update page:', error);
      alert('Failed to update page');
    }
  };

  const handleEditTour = (tour) => {
    setEditingItem(tour);
    setActiveTab('edit-tour');
    setPdfFile(null);
  };

  const handleSaveTour = async (e) => {
    e.preventDefault();
    try {
      await toursAPI.updateTour(editingItem.id, editingItem);
      if (pdfFile) {
        await toursAPI.uploadTourPdf(editingItem.id, pdfFile);
      }
      alert('Tour updated successfully!');
      loadData();
      setActiveTab('tours');
      setEditingItem(null);
      setPdfFile(null);
    } catch (error) {
      console.error('Failed to update tour:', error);
      alert('Failed to update tour');
    }
  };

  const handleDeleteTour = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;
    
    try {
      await toursAPI.deleteTour(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete tour:', error);
      alert('Failed to delete tour');
    }
  };

  const handleDeleteTourPdf = async (id) => {
    if (!window.confirm('Remove attached PDF?')) return;
    try {
      await toursAPI.deleteTourPdf(id);
      alert('PDF removed.');
      loadData();
    } catch (error) {
      console.error('Failed to delete tour PDF:', error);
      alert('Failed to delete PDF');
    }
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    setUploadingPhoto(true);
    try {
      await photosAPI.uploadPhoto(formData);
      alert('Photo uploaded successfully!');
      loadData();
      e.target.reset();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleEditPhoto = (photo) => {
    setEditingItem(photo);
    setActiveTab('edit-photo');
  };

  const handleSavePhoto = async (e) => {
    e.preventDefault();
    try {
      await photosAPI.updatePhoto(editingItem.id, editingItem);
      alert('Photo updated successfully!');
      loadData();
      setActiveTab('photos');
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update photo:', error);
      alert('Failed to update photo');
    }
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo? This will also remove the file.')) return;
    
    try {
      await photosAPI.deletePhoto(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo');
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>AllegroTours CMS</h1>
          <div className="admin-actions">
            <a href="/" target="_blank" rel="noopener noreferrer" className="btn-view-site">
              View Site →
            </a>
            <button onClick={handleLogout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="admin-container">
        <nav className="admin-tabs">
          <button 
            className={activeTab === 'pages' ? 'active' : ''}
            onClick={() => setActiveTab('pages')}
          >
            Pages ({pages.length})
          </button>
          <button 
            className={activeTab === 'tours' ? 'active' : ''}
            onClick={() => setActiveTab('tours')}
          >
            Tours ({tours.length})
          </button>
          <button 
            className={activeTab === 'photos' ? 'active' : ''}
            onClick={() => setActiveTab('photos')}
          >
            Photos ({photos.length})
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button 
            className={activeTab === 'languages' ? 'active' : ''}
            onClick={() => setActiveTab('languages')}
          >
            Languages
          </button>
        </nav>

        <div className="admin-content">
          {activeTab === 'pages' && (
            <div className="content-section">
              <h2>Manage Pages</h2>
              <div className="items-grid">
                {pages.map(page => (
                  <div key={page.id} className="item-card">
                    <div className="item-header">
                      <h3>{page.title}</h3>
                      <span className="lang-badge">{page.language.toUpperCase()}</span>
                    </div>
                    <p className="item-meta">Slug: {page.slug}</p>
                    <p className="item-preview">{page.hero_heading}</p>
                    <button 
                      onClick={() => handleEditPage(page)}
                      className="btn-edit"
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'tours' && (
            <div className="content-section">
              <h2>Manage Tours</h2>
              <div className="items-grid">
                {tours.map(tour => (
                  <div key={tour.id} className="item-card">
                    <div className="item-header">
                      <h3>{tour.title}</h3>
                      <span className="lang-badge">{tour.language.toUpperCase()}</span>
                    </div>
                    {tour.subtitle && <p className="item-subtitle">{tour.subtitle}</p>}
                    {tour.pdf_filename && (
                      <p className="item-meta">
                        <a 
                          href={`/tour-pdfs/${tour.pdf_filename}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="link"
                        >
                          View PDF
                        </a>
                      </p>
                    )}
                    <div className="item-actions">
                      <button 
                        onClick={() => handleEditTour(tour)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteTour(tour.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="content-section">
              <h2>Manage Photos</h2>
              
              <div className="upload-section">
                <h3>Upload New Photo</h3>
                <form onSubmit={handlePhotoUpload} className="upload-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Photo File *</label>
                      <input
                        type="file"
                        name="photo"
                        accept="image/*"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Alt Text</label>
                      <input
                        type="text"
                        name="alt_text"
                        placeholder="Description for accessibility"
                      />
                    </div>
                    <div className="form-group">
                      <label>Caption</label>
                      <input
                        type="text"
                        name="caption"
                        placeholder="Photo caption"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Display Order</label>
                      <input
                        type="number"
                        name="display_order"
                        defaultValue="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn-save" disabled={uploadingPhoto}>
                    {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </button>
                </form>
              </div>

              <div className="photos-grid">
                {photos.map(photo => (
                  <div key={photo.id} className="photo-card">
                    <img 
                      src={`/photos/${photo.filename}`} 
                      alt={photo.alt_text || photo.original_name}
                      className="photo-thumbnail"
                    />
                    <div className="photo-info">
                      <p className="photo-name">{photo.original_name}</p>
                      {photo.caption && <p className="photo-caption">{photo.caption}</p>}
                      <div className="photo-meta">
                        <span className={`status-badge ${photo.is_active ? 'active' : 'inactive'}`}>
                          {photo.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="order-badge">Order: {photo.display_order}</span>
                      </div>
                    </div>
                    <div className="photo-actions">
                      <button 
                        onClick={() => handleEditPhoto(photo)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'edit-page' && editingItem && (
            <div className="content-section">
              <div className="section-header">
                <h2>Edit Page: {editingItem.title}</h2>
                <button 
                  onClick={() => { setActiveTab('pages'); setEditingItem(null); }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
              
              <form onSubmit={handleSavePage} className="edit-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={editingItem.title || ''}
                      onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Sublogo Text</label>
                  <input
                    type="text"
                    value={editingItem.sublogo_text || ''}
                    onChange={(e) => setEditingItem({...editingItem, sublogo_text: e.target.value})}
                    placeholder="Text shown under the logo in header"
                  />
                </div>

                <div className="form-group">
                  <label>Hero Heading</label>
                  <textarea
                    value={editingItem.hero_heading || ''}
                    onChange={(e) => setEditingItem({...editingItem, hero_heading: e.target.value})}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Hero Subheading</label>
                  <textarea
                    value={editingItem.hero_subheading || ''}
                    onChange={(e) => setEditingItem({...editingItem, hero_subheading: e.target.value})}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Partner Text 1</label>
                  <textarea
                    value={editingItem.partner_text_top ?? editingItem.partner_text ?? ''}
                    onChange={(e) => setEditingItem({
                      ...editingItem,
                      partner_text_top: e.target.value
                    })}
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>Partner Text 2</label>
                  <textarea
                    value={editingItem.partner_text_bottom ?? ''}
                    onChange={(e) => setEditingItem({
                      ...editingItem,
                      partner_text_bottom: e.target.value
                    })}
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>Offers Heading</label>
                  <input
                    type="text"
                    value={editingItem.offers_heading || ''}
                    onChange={(e) => setEditingItem({...editingItem, offers_heading: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Accommodations / CTA Text</label>
                  <textarea
                    value={editingItem.accommodations_text || ''}
                    onChange={(e) => setEditingItem({...editingItem, accommodations_text: e.target.value})}
                    rows="4"
                  />
                </div>

                <h3 className="form-section-title">SEO</h3>

                <div className="form-group">
                  <label>Meta Description</label>
                  <textarea
                    value={editingItem.meta_description || ''}
                    onChange={(e) => setEditingItem({...editingItem, meta_description: e.target.value})}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Meta Keywords (comma separated)</label>
                  <input
                    type="text"
                    value={editingItem.meta_keywords || ''}
                    onChange={(e) => setEditingItem({...editingItem, meta_keywords: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Custom Meta Tags (HTML)</label>
                  <textarea
                    value={editingItem.meta_tags || ''}
                    onChange={(e) => setEditingItem({...editingItem, meta_tags: e.target.value})}
                    rows="6"
                    placeholder={`<meta property="og:title" content="..." />\n<meta property="og:description" content="..." />\n<meta name="twitter:card" content="summary_large_image" />`}
                  />
                </div>

                <h3 className="form-section-title">Contact Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      type="text"
                      value={editingItem.contact_name || ''}
                      onChange={(e) => setEditingItem({...editingItem, contact_name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editingItem.contact_email || ''}
                      onChange={(e) => setEditingItem({...editingItem, contact_email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editingItem.contact_phone || ''}
                      onChange={(e) => setEditingItem({...editingItem, contact_phone: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile</label>
                    <input
                      type="tel"
                      value={editingItem.contact_mobile || ''}
                      onChange={(e) => setEditingItem({...editingItem, contact_mobile: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Fax</label>
                    <input
                      type="tel"
                      value={editingItem.contact_fax || ''}
                      onChange={(e) => setEditingItem({...editingItem, contact_fax: e.target.value})}
                    />
                  </div>
                </div>

                <button type="submit" className="btn-save">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'edit-tour' && editingItem && (
            <div className="content-section">
              <div className="section-header">
                <h2>Edit Tour: {editingItem.title}</h2>
                <button 
                  onClick={() => { setActiveTab('tours'); setEditingItem(null); }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
              
              <form onSubmit={handleSaveTour} className="edit-form">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editingItem.title || ''}
                    onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Subtitle</label>
                  <input
                    type="text"
                    value={editingItem.subtitle || ''}
                    onChange={(e) => setEditingItem({...editingItem, subtitle: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                    rows="4"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={editingItem.display_order || 0}
                      onChange={(e) => setEditingItem({...editingItem, display_order: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={editingItem.is_active}
                        onChange={(e) => setEditingItem({...editingItem, is_active: e.target.checked})}
                      />
                      {' '}Active
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tour PDF (optional)</label>
                  {editingItem.pdf_filename ? (
                    <div className="pdf-row">
                      <a 
                        href={`/tour-pdfs/${editingItem.pdf_filename}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-link"
                      >
                        View current PDF
                      </a>
                      <button 
                        type="button" 
                        className="btn-delete small"
                        onClick={() => handleDeleteTourPdf(editingItem.id)}
                      >
                        Remove PDF
                      </button>
                    </div>
                  ) : (
                    <p className="note">No PDF uploaded yet.</p>
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPdfFile(e.target.files[0])}
                  />
                  {pdfFile && <p className="note">Selected: {pdfFile.name}</p>}
                </div>

                <button type="submit" className="btn-save">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'edit-photo' && editingItem && (
            <div className="content-section">
              <div className="section-header">
                <h2>Edit Photo: {editingItem.original_name}</h2>
                <button 
                  onClick={() => { setActiveTab('photos'); setEditingItem(null); }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
              
              <form onSubmit={handleSavePhoto} className="edit-form">
                <div className="photo-preview">
                  <img 
                    src={`/photos/${editingItem.filename}`} 
                    alt={editingItem.alt_text || editingItem.original_name}
                    style={{ maxWidth: '400px', borderRadius: '8px' }}
                  />
                </div>

                <div className="form-group">
                  <label>Alt Text</label>
                  <input
                    type="text"
                    value={editingItem.alt_text || ''}
                    onChange={(e) => setEditingItem({...editingItem, alt_text: e.target.value})}
                    placeholder="Description for accessibility"
                  />
                </div>

                <div className="form-group">
                  <label>Caption</label>
                  <input
                    type="text"
                    value={editingItem.caption || ''}
                    onChange={(e) => setEditingItem({...editingItem, caption: e.target.value})}
                    placeholder="Photo caption"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={editingItem.display_order || 0}
                      onChange={(e) => setEditingItem({...editingItem, display_order: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={editingItem.is_active}
                        onChange={(e) => setEditingItem({...editingItem, is_active: e.target.checked})}
                      />
                      {' '}Active
                    </label>
                  </div>
                </div>

                <button type="submit" className="btn-save">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'settings' && (
            <Settings />
          )}

          {activeTab === 'languages' && (
            <Languages />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
